/* assets/src/viewer/core/scene.js
 * SceneManager: scena persistente + terreno (riceve ombre) + contact-shadow/AO a terra
 * + griglie opzionali. Aggrega Lighting e SkyEnvironment. Nessun dispose/rebuild della
 * scena: il modello è l'unico gruppo che cambia (gestito dal PergolaController).
 */
import * as THREE from 'three';
import { SkyEnvironment } from '../env/sky.js';
import { Lighting } from './lighting.js';

function makeRadialAlphaTexture(size = 256) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createRadialGradient(size / 2, size / 2, size * 0.05, size / 2, size / 2, size * 0.5);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.55, 'rgba(255,255,255,0.55)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.userData.asgShared = true;
  return tex;
}

export class SceneManager {
  constructor(renderer) {
    this.renderer = renderer;
    this.onNeedsRender = null; // impostato dal Viewer: invalida il render (asset async)
    this.scene = new THREE.Scene();

    this.skyEnv = new SkyEnvironment(renderer, this.scene);
    this.lighting = new Lighting(this.scene, renderer);
    this.lighting.setSky(this.skyEnv);

    this.ground = this._buildGround();
    this.scene.add(this.ground);

    this.contactShadow = this._buildContactShadow();
    this.scene.add(this.contactShadow);

    this._buildGrids();

    // Ambiente + luci iniziali (genera la prima env-map dal cielo).
    this.lighting.applyState({ regenEnv: true });
  }

  _buildGround() {
    const geo = new THREE.PlaneGeometry(220, 220);
    const mat = new THREE.MeshStandardMaterial({ color: 0xb6b0a6, roughness: 0.95, metalness: 0.0 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0;
    mesh.receiveShadow = true;
    mesh.userData.asgGround = true;
    this._applyGroundTexture(mat);
    return mesh;
  }

  // Terreno PBR reale (CC0): decking in legno. Caricato async; invalida il render al pronto.
  _applyGroundTexture(mat) {
    const base = (window.ASG_Data && window.ASG_Data.groundBase) || 'assets/textures/ground/';
    const loader = new THREE.TextureLoader();
    const maxAniso = this.renderer.capabilities.getMaxAnisotropy ? this.renderer.capabilities.getMaxAnisotropy() : 8;
    const REPEAT = 150; // ~1.45 m per doga su un piano 220 m
    const cfg = (tex, srgb) => {
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(REPEAT, REPEAT);
      tex.anisotropy = maxAniso;
      tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      return tex;
    };
    const done = () => { mat.needsUpdate = true; if (this.onNeedsRender) this.onNeedsRender(); };
    loader.load(base + 'deck_diff.webp', (t) => { mat.map = cfg(t, true); mat.color.set(0xffffff); done(); });
    loader.load(base + 'deck_nor.webp', (t) => { mat.normalMap = cfg(t, false); mat.normalScale.set(0.7, 0.7); done(); });
    loader.load(base + 'deck_arm.webp', (t) => { mat.roughnessMap = cfg(t, false); mat.roughness = 1.0; done(); });
  }

  _buildContactShadow() {
    const tex = makeRadialAlphaTexture(256);
    const mat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      alphaMap: tex,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      toneMapped: false,
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0.012;
    mesh.renderOrder = 1;
    mesh.visible = false;
    return mesh;
  }

  // Ridimensiona/posiziona il contact-shadow sotto l'ingombro del modello.
  fitContactShadow(box) {
    if (!box || box.isEmpty()) { this.contactShadow.visible = false; return; }
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const footprint = Math.max(size.x, size.z) * 1.22;
    this.contactShadow.scale.set(footprint, footprint, 1);
    this.contactShadow.position.set(center.x, 0.012, center.z);
    this.contactShadow.visible = true;
  }

  _buildGrids() {
    // Griglie disponibili per compatibilità (window.gridHelper*) ma nascoste:
    // su terreno realistico l'aspetto CAD è indesiderato.
    const minor = new THREE.GridHelper(80, 80, 0x9aa6b6, 0x9aa6b6);
    minor.position.y = 0.006;
    minor.material.transparent = true;
    minor.material.opacity = 0.14;
    minor.material.depthWrite = false;
    minor.visible = false;
    this.scene.add(minor);
    this.gridMinor = minor;

    const major = new THREE.GridHelper(80, 16, 0x7fb5ff, 0x6c7a8c);
    major.position.y = 0.01;
    major.material.transparent = true;
    major.material.opacity = 0.25;
    major.material.depthWrite = false;
    major.visible = false;
    this.scene.add(major);
    this.gridMajor = major;
  }

  setGroundVisible(v) {
    this.ground.visible = !!v;
  }
}
